import Select, { OptionProps } from 'react-select';
import i18next from 'i18next';
import MotornetVehicleSelector, { MotornetSelectedVehicle, getMakeName } from '../../../motornet/common/MotornetVehicleSelector';
import useLeadsStore from '../../../store/LeadsStore';
import SpinnerButton from '../../SpinnerButton/SpinnerButton';
import useModalStore from '../../../store/ModalStore';
import SelectOptWithSubtitle from '../../SelectOptWithSubtitle/SelectOptWithSubtitle';
import RequiredFormLabel from '../../RequiredFormLabel/RequiredFormLabel';
import { Formik } from 'formik';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { LeadCreateRequestDTO } from '../../../api/models/Leads/LeadCreateRequestDTO';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
	ClienteEnum,
	LeadsModalModeEnum,
	LeadStatusEnum,
	RoleEnum,
	TabsEnum,
} from '../../../api/models/Enums';
import { useCallback, useMemo, useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { ReactSelectOption } from '../../../types';
import { formatDate } from '../../../utils';
import { useAuth } from '../../../hooks/useAuth';
import {
	translateLeadMotivation,
	translateSfAccountsTypes,
} from '../../../utils/translations';
import { useSearchParams } from 'react-router-dom';
import '../../../pages/Leads/LeadsPage.scss';
import { FaInfoCircle } from 'react-icons/fa';
import ToolTip from 'src/components/Tooltip/Tooltip';

interface LeadFormDataProps {
	initialValues: LeadCreateRequestDTO;
	submitFn: any;
	leadSourceOptions?: ReactSelectOption[];
	fonteAcquisizioneOptions?: ReactSelectOption[];
	validationSchema: any;
}
interface UserSalesOption extends ReactSelectOption {
	subtitle?: string;
}

const LeadFormData: React.FC<LeadFormDataProps> = ({
	initialValues,
	submitFn,
	leadSourceOptions = [],
	fonteAcquisizioneOptions = [],
	validationSchema,
}) => {
	const { t } = useTranslation();
	const { apiClient } = useApiClient();
	const { modalState, closeModal } = useModalStore();
	const { activeLead, userSalesOptions, activeTab, cleanActiveLead } =
		useLeadsStore();
	const { userRole, user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const handleClose = () => {
		closeModal();
		cleanActiveLead();
	};
	const [selectedVehicle, setSelectedVehicle] = useState<MotornetSelectedVehicle>({
		brand: null,
		model: null,
		version: null,
	});

	const handleFormSubmit = (values: LeadCreateRequestDTO) => {
		const { LastModifiedDate, ...filteredValues } = values;
		const motivationEdit =
			LeadStatusEnum.UNQUALIFIED === activeLead?.Status
				? activeLead?.Motivazione__c
				: '';

		// API CALL EDIT LEAD
		if (modalState.mode === LeadsModalModeEnum.DETAILS) {
			const editLeadPayload = {
				...filteredValues,
				Motivazione__c: motivationEdit,
				Status: activeLead.Status,
			};
			submitFn.mutate(editLeadPayload);
		}
		// API CALL CREATE LEAD
		if (modalState.mode === LeadsModalModeEnum.CREATE_NEW_LEAD) {
			const payloadCreateLead = {
				...filteredValues,
				SalesUser: [
					RoleEnum.SALES,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
				].includes(userRole)
					? user.username
					: values.SalesUser,
			};
			submitFn.mutate(payloadCreateLead);
		}
		handleClose();
		setSearchParams({}, { replace: true });
	};

	const { data: clientTypeOptions } = useQuery({
		queryKey: ['client_type_fields'],
		queryFn: async (): Promise<ReactSelectOption[]> => {
			const response = await apiClient.leadsControllerService.getLeadFields();
			const clientTypeOptions = response
				.find((item) => item.name === 'Cliente__c')
				?.picklistValues.filter((item) => item.active)
				.map((item) => {
					return {
						value: item.value,
						label: translateSfAccountsTypes(item.label),
					};
				});
			return clientTypeOptions || [];
		},
		staleTime: 1000 * 60 * 5,
		refetchOnWindowFocus: false,
		retry: false,
		refetchOnMount: false,
	});

	const isFiscalCodeRequired = useCallback(
		(values) => {
			const isCreateOrDetailMode = [
				LeadsModalModeEnum.CREATE_NEW_LEAD,
				LeadsModalModeEnum.DETAILS,
			].includes(modalState.mode);

			if (isCreateOrDetailMode) return false;

			const isPrivate = [ClienteEnum.Privato].includes(
				values.Cliente__c as ClienteEnum
			);

			return isPrivate && !values.Codice_Fiscale__c;
		},
		[modalState.mode]
	);

	const isBusinessFiscalCodeRequired = useCallback(
		(values) => {
			const isCreateOrDetailMode = [
				LeadsModalModeEnum.CREATE_NEW_LEAD,
				LeadsModalModeEnum.DETAILS,
			].includes(modalState.mode);

			if (isCreateOrDetailMode) return false;

			const isBusiness = [
				ClienteEnum.Libero_Professionista,
				ClienteEnum.Azienda,
			].includes(values.Cliente__c as ClienteEnum);

			return isBusiness && !values.Partita_Iva__c;
		},
		[modalState.mode]
	);

	const conversionInputTooltip = useMemo(() => {
		if (activeTab !== TabsEnum.TO_BE_CONVERTED) return null;
		return (
			<small id="info-die-required" className="text-muted ms-2">
				<FaInfoCircle size={18} />
				<ToolTip
					anchorId="info-die-required"
					content={t('field_required_for_lead_conversion')}
					className="default"
				/>
			</small>
		);
	}, [activeTab, t]);

	return (
		<div className="mb-5">
			{initialValues.LastModifiedDate && (
				<div className="last_modified_date">
					{t('last_edit')}:{' '}
					{formatDate(initialValues.LastModifiedDate || '', 'it-IT', true)}
				</div>
			)}

			{activeLead?.Motivazione__c && activeTab === TabsEnum.UNQUALIFIED_LEADS && (
				<div className="last_modified_date">
					{t('unqualification_motivation')}:{' '}
					{translateLeadMotivation(activeLead?.Motivazione__c)}
				</div>
			)}

			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleFormSubmit}
				enableReinitialize={true}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleSubmit,
					handleBlur,
					setFieldValue,
				}) => {
					const isBusinessClient = values?.Cliente__c
						? [ClienteEnum.Azienda, ClienteEnum.Libero_Professionista].includes(
							values.Cliente__c
						)
						: false;
					const isPrivateClient = values?.Cliente__c === ClienteEnum.Privato;
					const isStoreLeadSource = values?.LeadSource?.toLowerCase() === 'store';
					return (
						<Form onSubmit={handleSubmit}>
							<Row>
								<Col sm={6}>
									<Form.Group className="mb-3 mt-3">
										<RequiredFormLabel
											label={t('client_type')}
											required={!values.Cliente__c}
										/>
										<Select
											menuPosition="fixed"
											options={clientTypeOptions}
											onChange={(selected) => {
												setFieldValue(
													'Cliente__c',
													selected ? selected.value : null
												);
											}}
											value={
												clientTypeOptions?.find(
													(option) => (values.Cliente__c ?? '') === option.value
												) || null
											}
											className={`custom-select ${touched.Cliente__c && errors.Cliente__c
													? 'select-error'
													: ''
												}`}
										/>
										{touched.Cliente__c && errors.Cliente__c ? (
											<div style={{ color: 'red' }}>{errors.Cliente__c}</div>
										) : null}
									</Form.Group>
								</Col>

								{modalState.mode === LeadsModalModeEnum.DETAILS && (
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<Form.Label>{t('dealer')}</Form.Label>
											<Form.Control
												type="text"
												name="Filiale_Dealer_Titolare__c"
												onChange={handleChange}
												value={values.Filiale_Dealer_Titolare__c ?? ''}
												isInvalid={
													!!errors.Filiale_Dealer_Titolare__c &&
													touched.Filiale_Dealer_Titolare__c
												}
												disabled={true}
											/>
										</Form.Group>
									</Col>
								)}

								{modalState.mode === LeadsModalModeEnum.CREATE_NEW_LEAD &&
									[
										RoleEnum.BACKOFFICE,
										RoleEnum.MASTER,
										RoleEnum.ADMIN,
										RoleEnum.AREA_MANAGER,
									].includes(userRole) && (
										<Col sm={6}>
											<Form.Group className="mb-3 mt-3">
												<RequiredFormLabel
													label={t('sales_user')}
													required={!values.SalesUser}
												/>
												<Select<UserSalesOption, false>
													menuPosition="fixed"
													options={userSalesOptions}
													name="SalesUser"
													onChange={(option) =>
														setFieldValue(
															'SalesUser',
															option ? option.value : null
														)
													}
													value={
														userSalesOptions.find(
															(option) => option.value === values.SalesUser
														) || null
													}
													components={{
														Option: (props: OptionProps<UserSalesOption>) => (
															<SelectOptWithSubtitle {...props} />
														),
													}}
													isDisabled={submitFn.isPending}
												/>
												{touched.SalesUser && errors.SalesUser ? (
													<div style={{ color: 'red' }}>{errors.SalesUser}</div>
												) : null}
											</Form.Group>
										</Col>
									)}
							</Row>

							<Row>
								<Col sm={6}>
									<Form.Group className="mb-3">
										<RequiredFormLabel
											label={t('first_name')}
											required={
												[
													ClienteEnum.Libero_Professionista,
													ClienteEnum.Privato,
												].includes(values.Cliente__c as ClienteEnum) &&
												!values.FirstName
											}
										/>
										<Form.Control
											type="text"
											name="FirstName"
											onChange={handleChange}
											value={values.FirstName ?? ''}
											isInvalid={!!errors.FirstName && touched.FirstName}
										/>
										{touched.FirstName && errors.FirstName ? (
											<div style={{ color: 'red' }}>{errors.FirstName}</div>
										) : null}
									</Form.Group>
								</Col>

								<Col sm={6}>
									<Form.Group className="mb-3">
										<RequiredFormLabel
											label={t('last_name')}
											required={!values.LastName}
										/>
										<Form.Control
											type="text"
											name="LastName"
											onChange={handleChange}
											value={values.LastName ?? ''}
											isInvalid={!!errors.LastName && touched.LastName}
										/>
										{touched.LastName && errors.LastName ? (
											<div style={{ color: 'red' }}>{errors.LastName}</div>
										) : null}
									</Form.Group>
								</Col>
							</Row>

							<Row>
								{values.Cliente__c === ClienteEnum.Azienda && (
									<Col sm={6}>
										<Form.Group className="mb-3">
											<RequiredFormLabel
												label={t('company')}
												required={
													[ClienteEnum.Azienda].includes(
														values.Cliente__c as ClienteEnum
													) && !values.Company
												}
											/>
											<Form.Control
												type="text"
												name="Company"
												onChange={handleChange}
												value={values.Company ?? ''}
												isInvalid={!!errors.Company && touched.Company}
											/>
											{touched.Company && errors.Company ? (
												<div style={{ color: 'red' }}>{errors.Company}</div>
											) : null}
										</Form.Group>
									</Col>
								)}

								<Col sm={6}>
									<Form.Group className="mb-3">
										<RequiredFormLabel
											label={t('email')}
											required={!values.Email}
										/>
										<Form.Control
											type="email"
											name="Email"
											onChange={handleChange}
											value={values.Email ?? ''}
											isInvalid={!!errors.Email && touched.Email}
										/>
										{touched.Email && errors.Email ? (
											<div style={{ color: 'red' }}>{errors.Email}</div>
										) : null}
									</Form.Group>
								</Col>
							</Row>

							<Row>
								<Col sm={6}>
									<Form.Group className="mb-3">
										<RequiredFormLabel
											label={t('mobile_phone')}
											required={!values.Phone}
										/>
										<Form.Control
											type="text"
											name="Phone"
											onChange={handleChange}
											value={values.Phone || ''}
											isInvalid={!!errors.Phone && touched.Phone}
										/>
										{touched.Phone && errors.Phone ? (
											<div style={{ color: 'red' }}>{errors.Phone}</div>
										) : null}
									</Form.Group>
								</Col>

								<Col sm={6}>
									<Form.Group className="mb-3">
										<RequiredFormLabel
											label={t('city')}
											required={!values.City}
										/>
										<Form.Control
											type="text"
											name="City"
											onChange={handleChange}
											value={values.City || ''}
											isInvalid={!!errors.City && touched.City}
										/>
										{touched.City && errors.City ? (
											<div style={{ color: 'red' }}>{errors.City}</div>
										) : null}
									</Form.Group>
								</Col>
							</Row>

							<Row>
								{/* CODICE FISCALE */}
								{isPrivateClient && (
									<Col sm={6}>
										<Form.Group className="mb-3">
											<RequiredFormLabel
												label={
													<>
														{t('fiscal_code')}
														{conversionInputTooltip}
													</>
												}
												required={isFiscalCodeRequired(values)}
											/>
											<Form.Control
												type="text"
												name="Codice_Fiscale__c"
												onChange={handleChange}
												value={values.Codice_Fiscale__c || ''}
												isInvalid={
													!!errors.Codice_Fiscale__c &&
													touched.Codice_Fiscale__c
												}
											/>
											{touched.Codice_Fiscale__c && errors.Codice_Fiscale__c ? (
												<div style={{ color: 'red' }}>
													{errors.Codice_Fiscale__c}
												</div>
											) : null}
										</Form.Group>
									</Col>
								)}

								{/* PARTITA IVA */}
								{isBusinessClient && (
									<Col sm={6}>
										<Form.Group className="mb-3">
											<RequiredFormLabel
												label={
													<>
														{t('business_fiscal_code')}
														{conversionInputTooltip}
													</>
												}
												required={isBusinessFiscalCodeRequired(values)}
											/>
											<Form.Control
												type="text"
												name="Partita_Iva__c"
												onChange={handleChange}
												value={values.Partita_Iva__c || ''}
												isInvalid={
													!!errors.Partita_Iva__c && touched.Partita_Iva__c
												}
											/>
											{touched.Partita_Iva__c && errors.Partita_Iva__c ? (
												<div style={{ color: 'red' }}>
													{errors.Partita_Iva__c}
												</div>
											) : null}
										</Form.Group>
									</Col>
								)}
							</Row>

							<Row>
								{modalState.mode === LeadsModalModeEnum.CREATE_NEW_LEAD && (
									<>
										<RequiredFormLabel
											label={t('select_vehicle')}
											required={!values.Modello__c && !values.Marca__c}
										/>
										<MotornetVehicleSelector
											selectedVehicle={selectedVehicle}
											onBrandChange={(brand) => {
												setSelectedVehicle({
													brand,
													model: null,
													version: null,
												});
												setFieldValue('Marca__c', getMakeName(brand));
												setFieldValue('Modello__c', '');
											}}
											onModelChange={(model) => {
												setSelectedVehicle((prev) => ({ ...prev, model, version: null }));
												const modelName = model?.data?.gammaModello?.descrizione || model?.label || '';
												setFieldValue('Modello__c', modelName);
											}}
											onVersionChange={() => { }}
											hasBrandError={touched.Marca__c && !!errors.Marca__c}
											hasModelError={touched.Modello__c && !!errors.Modello__c}
										/>
										{(touched.Marca__c && errors.Marca__c) ||
											(touched.Modello__c && errors.Modello__c) ? (
											<div style={{ color: 'red' }}>
												{errors.Marca__c || errors.Modello__c}
											</div>
										) : null}
									</>
								)}

								{modalState.mode === LeadsModalModeEnum.DETAILS && (
									<>
										<Col sm={6}>
											<Form.Group className="mb-3">
												<Form.Label>{t('brand')}</Form.Label>
												<Form.Control
													type="text"
													name="Marca__c"
													onChange={handleChange}
													value={values.Marca__c || ''}
												/>
												{touched.Marca__c && errors.Marca__c ? (
													<div style={{ color: 'red' }}>{errors.Marca__c}</div>
												) : null}
											</Form.Group>
										</Col>

										<Col sm={6}>
											<Form.Group className="mb-3">
												<Form.Label>
													{t('model')}
													<small
														id="model-required-for-nurturing"
														className="text-muted ms-2"
													>
														<FaInfoCircle size={18} />
														<ToolTip
															anchorId="model-required-for-nurturing"
															content={t('model_required_for_nurturing')}
															className="default"
														/>
													</small>
												</Form.Label>
												<Form.Control
													type="text"
													name="Modello__c"
													onChange={(...args) => {
														handleChange(...args);
													}}
													value={values.Modello__c || ''}
												/>
											</Form.Group>
										</Col>
									</>
								)}
							</Row>

							<Row>
								<Col sm={6}>
									<Form.Group className="mb-3 mt-3">
										<RequiredFormLabel
											label={t('source')}
											required={!values.LeadSource}
										/>
										{modalState.mode === LeadsModalModeEnum.CREATE_NEW_LEAD && (
											<>
												<Select
													menuPosition="fixed"
													options={leadSourceOptions || []}
													onChange={(selected) => {
														const next = selected ? selected.value : null;
														if (next?.toLowerCase() != "store") {
															setFieldValue('Fonte_di_acquisizione__c', null);
														}
														setFieldValue('LeadSource', next);
													}}
													value={
														leadSourceOptions?.find(
															(option) =>
																(values.LeadSource ?? '') === option.value
														) || null
													}
													className={`custom-select ${touched.LeadSource && errors.LeadSource
															? 'select-error'
															: ''
														}`}
												/>
												{touched.LeadSource && errors.LeadSource ? (
													<div style={{ color: 'red' }}>
														{errors.LeadSource}
													</div>
												) : null}
											</>
										)}

										{modalState.mode === LeadsModalModeEnum.DETAILS && (
											<Form.Control
												type="text"
												name="LeadSource"
												value={values.LeadSource || ''}
												onChange={handleChange}
												disabled={
													modalState.mode === LeadsModalModeEnum.DETAILS
												}
											/>
										)}
									</Form.Group>
								</Col>
								{isStoreLeadSource &&  <Col sm={6}>
									<Form.Group className="mb-3 mt-3">
										<RequiredFormLabel
											label={t('fonte_acquisizione')}
											required={!values.Fonte_di_acquisizione__c && isStoreLeadSource && modalState.mode === LeadsModalModeEnum.CREATE_NEW_LEAD}
										/>
										{modalState.mode === LeadsModalModeEnum.CREATE_NEW_LEAD && (
											<>
												<Select
													menuPosition="fixed"
													options={fonteAcquisizioneOptions || []}
													onChange={(selected) => {
														setFieldValue(
															'Fonte_di_acquisizione__c',
															selected ? selected.value : null
														);
													}}
													value={
														fonteAcquisizioneOptions?.find(
															(option) =>
																(values.Fonte_di_acquisizione__c ?? '') === option.value
														) || null
													}
													className={`custom-select ${touched.Fonte_di_acquisizione__c && errors.Fonte_di_acquisizione__c
															? 'select-error'
															: ''
														}`}
												/>
												{touched.Fonte_di_acquisizione__c && errors.Fonte_di_acquisizione__c ? (
													<div style={{ color: 'red' }}>
														{errors.Fonte_di_acquisizione__c}
													</div>
												) : null}
											</>
										)}

										{modalState.mode === LeadsModalModeEnum.DETAILS && (
											<Form.Control
												type="text"
												name="Fonte_di_acquisizione__c"
												value={values.Fonte_di_acquisizione__c || ''}
												onChange={handleChange}
												disabled={
													modalState.mode === LeadsModalModeEnum.DETAILS
												}
											/>
										)}
									</Form.Group>
								</Col>}
								{modalState.mode === LeadsModalModeEnum.DETAILS && (
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<Form.Label>{t('chanel')}</Form.Label>
											<Form.Control
												type="text"
												name="Canale_del_lead__c"
												value={values.Canale_del_lead__c || ''}
												onChange={handleChange}
												disabled={true}
											/>
										</Form.Group>
									</Col>
								)}
							</Row>

							<Row>
								{modalState.mode === LeadsModalModeEnum.DETAILS && (
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<Form.Label>{t('utm_source')}</Form.Label>
											<Form.Control
												type="text"
												name="Utm_sorgente__c"
												value={values.Utm_sorgente__c || ''}
												onChange={handleChange}
												disabled={true}
											/>
										</Form.Group>
									</Col>
								)}
							</Row>

							<Row>
								<Col sm={12}>
									<Form.Group className="mb-3">
										<Form.Label>{t('notes')}</Form.Label>
										<Form.Control
											as="textarea"
											rows={2}
											name="Messaggio__c"
											onChange={handleChange}
											value={values.Messaggio__c || ''}
										/>
									</Form.Group>
								</Col>
							</Row>

							<Row>
								<Col md={12} className="d-flex justify-content-end">
									<Button
										variant="primary"
										type="submit"
										className="me-2"
										disabled={submitFn.isPending}
									>
										<SpinnerButton
											busy={submitFn.isPending}
											label={i18next.t('save')}
										/>
									</Button>

									<Button onClick={handleClose}>
										<SpinnerButton label={i18next.t('close')} />
									</Button>
								</Col>
							</Row>
						</Form>
					);
				}}
			</Formik>
		</div>
	);
};

export default LeadFormData;
